import { cn, Divider, Option, Select, Text } from '@youversion/design-system-components';
import { DownloadButton } from 'components/views/history/DownloadButton';
import { HistoryList } from 'components/views/history/HistoryList';
import { getYear } from 'date-fns';
import { useQueryParams } from 'hooks/useQueryParams';
import { useSelectorEvents } from 'hooks/useSelectorEvents';
import NextLink from 'next/link';
import { useTranslation } from 'next-i18next';
import { useEffect, useMemo, useState } from 'react';
import { firebaseLogEvent } from 'utils/firebase';

import { useGetUserQuery } from '@/graphql/user';

interface Props {
  onClose: () => void;
}

const givingHistoryYears = Array.from(
  {
    length: getYear(new Date()) - 2014 + 1,
  },
  (v, i) => 2014 + i
);

export default function GivingHistory({ onClose }: Props) {
  const { t } = useTranslation('common');
  const { hide_nav: hideNav } = useQueryParams();
  const today = useMemo(() => new Date(), []);
  const [selectedYear, setSelectedYear] = useState(today.getFullYear());
  const [showPopup, setShowPopup] = useState(false);

  const { data: userData } = useGetUserQuery();
  const { email, statementType } = userData?.user ?? {};

  const { logSelectorDismissedEvent, logSelectorPresentedEvent } = useSelectorEvents('giving_history_year');
  let timeoutId: NodeJS.Timeout | null = null;

  useEffect(() => {
    return () => {
      if (timeoutId) {
        clearTimeout(timeoutId);
      }
    };
  }, [showPopup, timeoutId]);

  function handleClick() {
    firebaseLogEvent('GivingButtonTap', { label: 'Download', screen: 'giving_history' });
    if (hideNav) {
      setShowPopup(true);
    }

    if (timeoutId) {
      clearTimeout(timeoutId);
    }

    timeoutId = setTimeout(() => {
      setShowPopup(false);
    }, 4000);
  }

  return (
    <>
      <div className='grow flex flex-col overflow-x-auto overflow-y-hidden'>
        <div className='flex justify-between pli-2'>
          <Select
            className={cn([
              'w-8 font-bold',
              '[&>*>select]:hover:cursor-pointer [&>*>select]:border-none [&>*>select]:pli-0 [&>*>select]:plb-1',
              '[&>*>div]:block-end-1 [&>*>div]:inline-end-0 [&>*>div]:text-text-light dark:[&>*>div]:text-text-dark',
            ])}
            name={t('giving.cta.select_year')}
            onChange={e => {
              firebaseLogEvent('GivingButtonTap', {
                label: 'Giving History Filter',
                screen: 'giving_history',
                value: e.currentTarget.value,
              });
              logSelectorDismissedEvent(e.currentTarget.value);
              setSelectedYear(parseInt(e.target.value));
            }}
            onClick={() => {
              firebaseLogEvent('GivingButtonTap', { label: 'Giving History Year', screen: 'giving_history' });
              firebaseLogEvent('GivingScreenView', { screen: 'giving_history_year' });
              logSelectorPresentedEvent(selectedYear);
            }}
            value={selectedYear}
          >
            {givingHistoryYears.map(year => (
              <Option key={year} label={year.toString()} value={year}>
                {year}
              </Option>
            ))}
          </Select>
          <DownloadButton onClick={handleClick} year={selectedYear} />
          {showPopup ? (
            <div className='fixed top-0 inset-x-3 dark:bg-canvas-secondary-light bg-canvas-dark p-2 border border-gray-20 rounded-1 flex justify-center z-10'>
              <Text className='dark:text-text-light text-text-dark' size='sm' variant='paragraph'>
                {t('giving.messages.statement_download')}
              </Text>
            </div>
          ) : null}
        </div>
        <div className='flex-1 flex flex-col plb-2 space-b-5 overflow-y-auto pli-2'>
          <HistoryList onClose={onClose} selectedYear={selectedYear} />
        </div>
      </div>
      <Divider className='w-full border-t-none border-b-small' />
      <div className='pbs-0.5 pbe-3 space-b-0.5 text-center pli-2'>
        {email ? (
          <Text className='text-text-muted-light dark:text-text-muted-dark' size='md' variant='caption'>
            {t('giving.messages.statement_emails', { email })}
          </Text>
        ) : null}
        <Text size='sm' variant='paragraph'>
          <NextLink
            href={`/statement-type${hideNav ? '?hide_nav=true' : ''}`}
            onClick={() => {
              firebaseLogEvent('GivingButtonTap', {
                label: 'Paper Statement',
                screen: 'giving_history',
                value: statementType === 'PAPER' ? 'Update' : 'Opt In',
              });
            }}
          >
            {statementType === 'PAPER'
              ? t('giving.cta.update_mailing_address')
              : t('giving.cta.paper_statement_sign_up')}
          </NextLink>
        </Text>
      </div>
    </>
  );
}
